import React, { useEffect, useRef, useState } from 'react'

// Smooth scrolling unless the user asked for reduced motion (jsdom and old
// browsers have no matchMedia: smooth).
export const scrollBehavior = () =>
  (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth')

// Scrolls only the pane the section scrolls in. scrollIntoView also scrolls
// every ancestor that clips (the admin's overflow-hidden frame included) to
// honour the section's scroll-margin, which shifted the whole app shell up
// and left a band below it.
export const scrollToSection = id => {
  const el = document.getElementById(id)
  if (!el) return
  const pane = scrollParent(el)
  if (!pane || !pane.scrollTo) {
    if (el.scrollIntoView) el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
    return
  }
  const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0
  const top = el.getBoundingClientRect().top - pane.getBoundingClientRect().top + pane.scrollTop - margin
  pane.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() })
}

// Scrolls the pane an element sits in so the element shows (block 'start'
// or 'center'), without scrolling the admin's frame (see scrollToSection).
export const scrollToElement = (el, { block = 'start' } = {}) => {
  if (!el) return
  const pane = scrollParent(el)
  if (!pane || !pane.scrollTo) {
    if (el.scrollIntoView) el.scrollIntoView({ behavior: scrollBehavior(), block })
    return
  }
  const rect = el.getBoundingClientRect()
  const paneRect = pane.getBoundingClientRect()
  const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0
  let top = rect.top - paneRect.top + pane.scrollTop - margin
  if (block === 'center') top -= Math.max(0, (pane.clientHeight - rect.height) / 2)
  pane.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() })
}

const CLICK_HOLD_MS = 1200

// The current section is the one with the most visual weight: the one that
// takes up the most of the visible area under the pinned header. rects are
// each section's {top, bottom} in viewport pixels, in page order; view is the
// visible band. Scrolled to the very top it's the first section, and to the
// very bottom the last (the final sections are often too short to ever
// outweigh the one above them). Ties go to the earlier section.
export const activeSection = (rects, view, { atTop = false, atBottom = false } = {}) => {
  if (!rects.length) return -1
  if (atBottom) return rects.length - 1
  if (atTop) return 0
  let best = 0
  let bestWeight = -1
  rects.forEach((r, i) => {
    const weight = Math.max(0, Math.min(view.bottom, r.bottom) - Math.max(view.top, r.top))
    if (weight > bestWeight) { best = i; bestWeight = weight }
  })
  return best
}

// The visible band: from under the workspace header when it is pinned
// (tablet and desktop), or the pane's top when it isn't (phones), down to the
// pane's bottom.
const visibleBand = scroller => {
  const pane = scroller.getBoundingClientRect()
  const head = scroller.querySelector('.sw-head')
  const pinned = head && window.getComputedStyle(head).position === 'sticky'
  return { top: pinned ? Math.max(pane.top, head.getBoundingClientRect().bottom) : pane.top, bottom: pane.bottom }
}

// The nearest ancestor that actually scrolls (the admin scrolls a pane, not
// the window).
const scrollParent = el => {
  for (let node = el && el.parentElement; node; node = node.parentElement) {
    const overflowY = window.getComputedStyle(node).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return node
  }
  return null
}

const DOT_TITLE = { ok: 'all good', warn: 'needs attention', bad: 'problem' }

// dots: { [sectionId]: 'ok' | 'warn' | 'bad' } -- the status-strip tiles'
// verdict for that section (status-strip.js sectionDots). A section with no
// verdict gets no dot.
// unsaved: { [sectionId]: { count, first } } -- the section's unsaved
// changes; shown as an "N unsaved" marker (distinct from the dot) that jumps
// to its first changed field (onJumpField).
export const SectionNav = ({ sections, mode, dots = {}, unsaved = {}, onJumpField }) => {
  const visible = sections.filter(s => mode !== 'customer' || !s.staff)
  const [active, setActive] = useState(visible[0] && visible[0].id)
  // A clicked link stays active while its smooth scroll plays out, so the
  // sections it passes don't flash by in the rail.
  const clickedAt = useRef(0)

  // Scroll-spy: on every scroll (at most once a frame), the current section
  // is decided from how much of each section is actually on screen, so the
  // rail follows what the reader is looking at, in both directions.
  useEffect(() => {
    const first = visible[0] && document.getElementById(visible[0].id)
    const scroller = scrollParent(first)
    if (!scroller) return undefined
    let frame = null
    const update = () => {
      frame = null
      if (Date.now() - clickedAt.current <= CLICK_HOLD_MS) return
      const rects = visible.map(s => { const el = document.getElementById(s.id); return el ? el.getBoundingClientRect() : { top: Infinity, bottom: Infinity } })
      const atTop = scroller.scrollTop <= 2
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
      const index = activeSection(rects, visibleBand(scroller), { atTop, atBottom })
      if (index >= 0) setActive(visible[index].id)
    }
    const onScroll = () => { if (frame === null) frame = window.requestAnimationFrame(update) }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => { scroller.removeEventListener('scroll', onScroll); if (frame !== null) window.cancelAnimationFrame(frame) }
  }, [visible.map(s => s.id).join()]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <nav className="sw-nav" aria-label="sections">
      {visible.map(s =>
        <div key={s.id} className="sw-nav-item">
          <a href={`#${s.id}`} className={active === s.id ? 'active' : ''} aria-current={active === s.id ? 'location' : undefined}
            onClick={e => { e.preventDefault(); clickedAt.current = Date.now(); setActive(s.id); scrollToSection(s.id) }}>
            {s.label}
            {DOT_TITLE[dots[s.id]] && <span className={`sw-dot ${dots[s.id]}`} data-testid={`sw-dot-${s.id}`} data-tone={dots[s.id]} title={DOT_TITLE[dots[s.id]]} aria-hidden="true" />}
          </a>
          {unsaved[s.id] && unsaved[s.id].count > 0 &&
            <button type="button" className="sw-nav-unsaved" data-testid={`sw-unsaved-${s.id}`}
              aria-label={`${s.label}: ${unsaved[s.id].count} unsaved, go to the first`}
              onClick={() => { clickedAt.current = Date.now(); setActive(s.id); onJumpField && onJumpField(unsaved[s.id].first) }}>
              {unsaved[s.id].count} unsaved
            </button>}
        </div>)}
    </nav>
  )
}
